import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import routerReducer from '../slice/routerSlice';
import AlertSlice from '../slice/AlertSlice';
import cartReducer from '../components/properties/property-view/slice/OrderCartSlice';
import imageAssetReducer from '../components/property-assets-management/slice/ImageAssetSlice';
import orderReducer from '../components/orders/order-confirmation/order-confirmation-store/orderSlice';
import { registrationApi } from '../components/registration/services/registrationApi';
import { helpSupportApi } from '../components/help-support/services/helpSupportApi';
import { teamApi } from '../components/manage-team/services/teamApi';
import { propertyApi } from '../components/properties/services/propertyApi';
import { orderApi } from '../components/orders/services/orderApi';
import { assetApi } from '../components/property-assets-management/services/assetApi';
import { orgSelectionApi } from '../components/organization-selection/services/orgSelectionApi';
import { accountViewApi } from '../components/accounts-console/services/accountViewApi';
import { promoCodesApi } from '../components/admin-promo-codes/services/promoCodesApi';
import { globalOrdersApi } from '../components/global-orders/services/globalOrdersApi';
import { pmsConnectionApi } from '../components/pms/services/pmsConnectionApi';
import pmsSyncReducer from '../components/pms/services/pmsSyncSlice';
import entityScopeReducer from '../slice/entityScopeSlice';
import { scopePersistenceMiddleware } from '../services/entity-scope-persistence';
import { registerStore } from './get-store';

export const store = configureStore({
  reducer: {
    router: routerReducer,
    alertState: AlertSlice,
    pmsSync: pmsSyncReducer,
    orderCart: cartReducer,
    imageAsset: imageAssetReducer,
    order: orderReducer,
    entityScope: entityScopeReducer,
    [registrationApi.reducerPath]: registrationApi.reducer,
    [helpSupportApi.reducerPath]: helpSupportApi.reducer,
    [teamApi.reducerPath]: teamApi.reducer,
    [propertyApi.reducerPath]: propertyApi.reducer,
    [orderApi.reducerPath]: orderApi.reducer,
    [assetApi.reducerPath]: assetApi.reducer,
    [orgSelectionApi.reducerPath]: orgSelectionApi.reducer,
    [promoCodesApi.reducerPath]: promoCodesApi.reducer,
    [globalOrdersApi.reducerPath]: globalOrdersApi.reducer,
    [accountViewApi.reducerPath]: accountViewApi.reducer,
    [pmsConnectionApi.reducerPath]: pmsConnectionApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(registrationApi.middleware)
      .concat(helpSupportApi.middleware)
      .concat(teamApi.middleware)
      .concat(propertyApi.middleware)
      .concat(orderApi.middleware)
      .concat(assetApi.middleware)
      .concat(orgSelectionApi.middleware)
      .concat(promoCodesApi.middleware)
      .concat(globalOrdersApi.middleware)
      .concat(accountViewApi.middleware)
      .concat(pmsConnectionApi.middleware)
      // The one writer of the scope keys (DV-3013 spec §3.2; action-aware
      // since DV-3573 — see its docstring).
      .concat(scopePersistenceMiddleware),
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Every RTK Query slice registered above. Exported so a test can assert this
 * list has not drifted from the store's actual reducers — the failure mode is
 * silent, and it leaves one cache serving pre-switch data.
 */
export const API_SLICES = [
  registrationApi,
  helpSupportApi,
  teamApi,
  propertyApi,
  orderApi,
  assetApi,
  orgSelectionApi,
  promoCodesApi,
  globalOrdersApi,
  accountViewApi,
  pmsConnectionApi,
] as const;

/**
 * Drop every cached query and mutation result across all api slices.
 *
 * Needed by every `switchEntity` propagation mode (DV-3013 §3.4 / D5); it
 * was `'continue'`-only under DV-3012 / D6.
 * The other three modes end in a full-page navigation, so the cache dies with
 * the document; nothing reloads any more, and the two requests the switch
 * itself made were issued on the PRE-switch token. Without this, the first
 * render under the new scope reads responses fetched under the old one.
 *
 * `resetApiState()` is per-slice, hence the list. Phase C (DV-3013) reuses
 * this to replace reload-to-propagate on every switch.
 */
export const resetAllApiState = (): void => {
  for (const api of API_SLICES) {
    store.dispatch(api.util.resetApiState());
  }
};

// Registered last, after `store` and `resetAllApiState` both exist. This is the
// inversion that lets entity-scope-api.ts and entity-scope.ts reach the store
// without a module-scope import of THIS file — see get-store.ts's docstring.
registerStore(store, { resetAllApiState });
